import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useScrollContainer, useScrollToTop } from '@/components/layout/scroll-container';
import { cn } from '@/lib/utils';

/** Floating "back to top" button for long agent result pages. Fades in
 *  once the user has scrolled past 400px and disappears near the top.
 *  Fixed to the bottom-right corner of the viewport. Shared across all
 *  5 agent result routes (predicate, strategy, dossier, clinical-
 *  protocol, clinical-report) so the back-to-top affordance is
 *  consistent everywhere.
 *
 *  Scroll source: the AppShell's content container (the pinned shell
 *  means the window never scrolls); falls back to the window when
 *  rendered outside the shell. */
export function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  const containerRef = useScrollContainer();
  const scrollToTop = useScrollToTop();

  useEffect(() => {
    const el = containerRef?.current;
    const target: HTMLElement | Window = el ?? window;
    const onScroll = () => {
      setVisible((el ? el.scrollTop : window.scrollY) > 400);
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => target.removeEventListener('scroll', onScroll);
  }, [containerRef]);

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Scroll to top of page"
      tabIndex={visible ? 0 : -1}
      className={cn(
        'fixed bottom-6 right-12 z-50',
        'size-11 rounded-full',
        'bg-navy text-white shadow-card',
        'flex items-center justify-center',
        'hover:bg-electric hover:shadow-[0_8px_20px_rgba(0,192,255,0.3)]',
        'focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2',
        'transition-all duration-200',
        visible
          ? 'opacity-100 pointer-events-auto translate-y-0'
          : 'opacity-0 pointer-events-none translate-y-2',
      )}
    >
      <ArrowUp className="size-5" aria-hidden="true" />
    </button>
  );
}
